import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Calendar } from "./ui/calendar";
import { toast } from "sonner";
import { TIME_SLOTS } from "../data/site";
import { submitMeeting, getAvailability } from "../lib/api";

const initial = {
    name: "",
    email: "",
    phone: "",
    mode: "video",
    notes: "",
};

const MODES = [
    { value: "video", label: "Video call" },
    { value: "phone", label: "Phone call" },
    { value: "in-person", label: "Studio visit" },
];

const fmtDate = (d) => {
    if (!d) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

export const MeetingDialog = ({ open, onOpenChange }) => {
    const [form, setForm] = useState(initial);
    const [date, setDate] = useState(undefined);
    const [slot, setSlot] = useState("");
    const [booked, setBooked] = useState([]);
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

    useEffect(() => {
        if (!date) {
            setBooked([]);
            return;
        }
        getAvailability(fmtDate(date))
            .then((r) => setBooked(r.booked || []))
            .catch(() => setBooked([]));
    }, [date]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone) {
            toast.error("Please add your name, email and phone.");
            return;
        }
        if (!date || !slot) {
            toast.error("Please pick a date and time slot.");
            return;
        }
        setLoading(true);
        try {
            await submitMeeting({
                ...form,
                date: fmtDate(date),
                time_slot: slot,
            });
            toast.success("Meeting booked. You'll get a confirmation shortly.");
            setForm(initial);
            setDate(undefined);
            setSlot("");
            onOpenChange(false);
        } catch (err) {
            toast.error("Could not book. Please try another slot.");
        } finally {
            setLoading(false);
        }
    };

    const isPast = (d) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return d < today;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="meeting-dialog"
                className="max-w-3xl bg-[#FAF9F6] border-[#1A1A1A]/15 rounded-none p-0 max-h-[90vh] overflow-y-auto"
            >
                <div className="p-8 md:p-10">
                    <DialogHeader>
                        <div className="overline text-[#8C7C6D] mb-3">
                            Quick meeting · 30 minutes
                        </div>
                        <DialogTitle className="font-serif text-3xl md:text-4xl font-light tracking-tight text-[#1A1A1A]">
                            Book a <span className="italic text-[#8C7C6D]">meeting</span>.
                        </DialogTitle>
                    </DialogHeader>

                    <form
                        onSubmit={submit}
                        className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-6"
                    >
                        {/* Left: form fields */}
                        <div className="space-y-6">
                            <Input
                                label="Full name *"
                                value={form.name}
                                onChange={set("name")}
                                testid="meeting-name"
                            />
                            <Input
                                label="Email *"
                                type="email"
                                value={form.email}
                                onChange={set("email")}
                                testid="meeting-email"
                            />
                            <Input
                                label="Phone *"
                                value={form.phone}
                                onChange={set("phone")}
                                testid="meeting-phone"
                            />

                            <div>
                                <label className="dd-label">Mode</label>
                                <div className="flex flex-wrap gap-2">
                                    {MODES.map((m) => (
                                        <button
                                            type="button"
                                            key={m.value}
                                            data-testid={`meeting-mode-${m.value}`}
                                            onClick={() => setForm((f) => ({ ...f, mode: m.value }))}
                                            className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${
                                                form.mode === m.value
                                                    ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]"
                                                    : "border-[#1A1A1A]/25 text-[#1A1A1A] hover:border-[#1A1A1A]"
                                            }`}
                                        >
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="dd-label">Notes</label>
                                <textarea
                                    data-testid="meeting-notes"
                                    rows={3}
                                    value={form.notes}
                                    onChange={set("notes")}
                                    className="dd-input"
                                    placeholder="Anything we should know before the call?"
                                />
                            </div>
                        </div>

                        {/* Right: date + slot */}
                        <div className="space-y-6">
                            <div>
                                <label className="dd-label">Select a date *</label>
                                <div className="border border-[#1A1A1A]/15 inline-block">
                                    <Calendar
                                        mode="single"
                                        selected={date}
                                        onSelect={setDate}
                                        disabled={isPast}
                                        data-testid="meeting-calendar"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="dd-label">Time slot *</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {TIME_SLOTS.map((s) => {
                                        const isBooked = booked.includes(s);
                                        const active = slot === s;
                                        return (
                                            <button
                                                key={s}
                                                type="button"
                                                disabled={isBooked || !date}
                                                onClick={() => setSlot(s)}
                                                data-testid={`meeting-slot-${s}`}
                                                className={`px-3 py-2 text-[11px] uppercase tracking-[0.12em] border transition-colors ${
                                                    active
                                                        ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]"
                                                        : isBooked || !date
                                                          ? "border-[#1A1A1A]/10 text-[#1A1A1A]/30 cursor-not-allowed"
                                                          : "border-[#1A1A1A]/25 hover:border-[#1A1A1A]"
                                                }`}
                                            >
                                                {s}
                                            </button>
                                        );
                                    })}
                                </div>
                                {!date && (
                                    <p className="mt-3 text-xs text-[#4A4643]/70">
                                        Pick a date to see available slots.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="lg:col-span-2 flex items-center justify-end gap-3 pt-4 border-t border-[#1A1A1A]/10">
                            <button
                                type="button"
                                data-testid="meeting-cancel"
                                onClick={() => onOpenChange(false)}
                                className="btn-ghost"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="meeting-submit"
                                className="btn-primary disabled:opacity-50"
                            >
                                {loading ? "Booking..." : "Confirm meeting"}
                            </button>
                        </div>
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const Input = ({ label, value, onChange, type = "text", testid }) => (
    <div>
        <label className="dd-label">{label}</label>
        <input
            data-testid={testid}
            type={type}
            value={value}
            onChange={onChange}
            className="dd-input"
        />
    </div>
);
